import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BadgeCheck, TrendingUp, FileText, Calendar, Activity, Info, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  formatFollowerCount, 
  formatNumberWithCommas,
  formatRelativeTime 
} from '@/utils/instagram';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuthImage } from '@/hooks/useAuthImage';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ProfileStatsCardProps {
  profile: {
    id: string;
    username: string;
    full_name: string | null;
    profile_pic_url: string | null;
    followers_count: number;
    following_count: number;
    posts_count: number;
    is_verified: boolean;
    is_private: boolean;
    scraped_at: string;
  };
  metrics?: {
    followersChange?: number;
    postsChange?: number;
    postsPerWeek?: number;
    postsGoal?: string;
    hasInsufficientData?: boolean;
    dataPointsCount?: number;
  };
  className?: string;
}

export const ProfileStatsCard = ({ profile, metrics, className }: ProfileStatsCardProps) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  const getInitials = (name: string | null, username: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return username.slice(0, 2).toUpperCase();
  };

  const profilePicUrl = useAuthImage(profile.profile_pic_url);

  const formatChange = (value: number) => {
    return value > 0 ? `+${value}` : `${value}`;
  };

  const getChangeColor = (value: number) => {
    if (value > 0) return 'text-green-600 dark:text-green-400';
    if (value < 0) return 'text-red-600 dark:text-red-400';
    return 'text-muted-foreground';
  };

  const getWeeklyGoalStatus = () => {
    const goal = metrics?.postsGoal ? Number(metrics.postsGoal) : null;
    const actual = metrics?.postsPerWeek ?? null;
    if (!goal || actual === null) return 'neutral';
    if (actual >= goal) return 'good';
    if (actual >= goal * 0.7) return 'warning';
    return 'bad';
  };

  const weeklyStatus = getWeeklyGoalStatus();

  const weeklyCardColors = {
    good: {
      bg: 'from-green-50 to-green-100/50 dark:from-green-950/30 dark:to-green-900/20',
      border: 'border-green-200/50 dark:border-green-800/50 hover:border-green-400 dark:hover:border-green-600',
      glow: 'bg-green-400/10',
      label: 'text-green-600 dark:text-green-400',
      badge: 'text-green-600 dark:text-green-400',
      badgeMuted: 'text-green-600/70 dark:text-green-400/70',
      spinner: 'text-green-500',
    },
    warning: {
      bg: 'from-rose-50 to-rose-100/50 dark:from-rose-950/30 dark:to-rose-900/20',
      border: 'border-rose-200/50 dark:border-rose-800/50 hover:border-rose-400 dark:hover:border-rose-600',
      glow: 'bg-rose-400/10',
      label: 'text-rose-600 dark:text-rose-400',
      badge: 'text-rose-600 dark:text-rose-400',
      badgeMuted: 'text-rose-600/70 dark:text-rose-400/70',
      spinner: 'text-rose-500',
    },
    bad: {
      bg: 'from-red-50 to-red-100/50 dark:from-red-950/30 dark:to-red-900/20',
      border: 'border-red-200/50 dark:border-red-800/50 hover:border-red-400 dark:hover:border-red-600',
      glow: 'bg-red-400/10',
      label: 'text-red-600 dark:text-red-400',
      badge: 'text-red-600 dark:text-red-400',
      badgeMuted: 'text-red-600/70 dark:text-red-400/70',
      spinner: 'text-red-500',
    },
    neutral: {
      bg: 'from-violet-50 to-violet-100/50 dark:from-violet-950/30 dark:to-violet-900/20',
      border: 'border-violet-200/50 dark:border-violet-800/50 hover:border-violet-400 dark:hover:border-violet-600',
      glow: 'bg-violet-400/10',
      label: 'text-violet-600 dark:text-violet-400',
      badge: 'text-violet-600 dark:text-violet-400',
      badgeMuted: 'text-violet-600/70 dark:text-violet-400/70',
      spinner: 'text-violet-500',
    },
  };

  const wc = weeklyCardColors[weeklyStatus];

  return (
    <div className={cn("glass-card rounded-2xl overflow-hidden transition-lift", className)} data-tour="profile-stats">
      <CardContent className="p-6 md:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative group">
              <Avatar className="h-20 w-20 ring-2 ring-border group-hover:ring-primary/30 transition-all">
                <AvatarImage 
                  src={profilePicUrl || undefined} 
                  alt={`${profile.username}'s profile`}
                  className="object-cover"
                />
                <AvatarFallback className="text-xl font-bold bg-primary/10 text-primary">
                  {getInitials(profile.full_name, profile.username)}
                </AvatarFallback>
              </Avatar>
              {profile.is_verified && (
                <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-1.5">
                  <BadgeCheck className="h-4 w-4 text-white" />
                </div>
              )}
            </div>
            
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-foreground">
                {profile.full_name || profile.username}
              </h3>
              <p className="text-sm text-muted-foreground">
                @{profile.username}
              </p>
              <Badge className="mt-2 bg-success/10 text-success border-success/20">
                <Activity className="h-3 w-3 mr-1" />
                {t('dashboard.publicAccount')}
              </Badge>
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/60 border border-border/40 text-[10px] md:text-xs text-muted-foreground font-medium">
              <Calendar className="w-3 h-3" />
              <span>{t('dashboard.last7Days')}</span>
            </div>
            <Button 
              className="self-start md:self-auto"
              variant="outline"
              size="sm"
              onClick={() => navigate('/analityka')}
            >
              {t('dashboard.viewAnalytics')}
            </Button>
          </div>
        </div>

        {/* Stats Grid with Premium Cards */}
        <div className="grid grid-cols-3 gap-2 md:gap-4">
          {/* Followers */}
          <div className="group p-3 md:p-5 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20 border-2 border-blue-200/50 dark:border-blue-800/50 hover:border-blue-400 dark:hover:border-blue-600 transition-all hover:scale-105 hover:shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-blue-400/10 rounded-full blur-2xl" />
            <div className="text-xl md:text-3xl font-bold text-foreground counter-animate">
              {formatFollowerCount(profile.followers_count)}
            </div>
            <div className="text-[10px] md:text-xs font-medium text-blue-600 dark:text-blue-400 mt-1">{t('dashboard.followers')}</div>
            {metrics?.followersChange !== undefined && (
              <div className={cn("text-[10px] md:text-xs font-bold mt-1 md:mt-2 flex items-center gap-0.5 md:gap-1 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full bg-white/50 dark:bg-black/20 w-fit", getChangeColor(metrics.followersChange))}>
                <TrendingUp className="h-2.5 w-2.5 md:h-3 md:w-3" />
                {formatChange(metrics.followersChange)}
              </div>
            )}
          </div>

          {/* Posts */}
          <div className="group p-3 md:p-5 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 border-2 border-amber-200/50 dark:border-amber-800/50 hover:border-amber-400 dark:hover:border-amber-600 transition-all hover:scale-105 hover:shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 bg-amber-400/10 rounded-full blur-2xl" />
            <div className="text-xl md:text-3xl font-bold text-foreground counter-animate">
              {formatNumberWithCommas(profile.posts_count)}
            </div>
            <div className="text-[10px] md:text-xs font-medium text-amber-600 dark:text-amber-400 mt-1">{t('dashboard.posts')}</div>
            {metrics?.postsChange !== undefined && (
              <div className={cn("text-[10px] md:text-xs font-bold mt-1 md:mt-2 flex items-center gap-0.5 md:gap-1 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full bg-white/50 dark:bg-black/20 w-fit", getChangeColor(metrics.postsChange))}>
                <TrendingUp className="h-2.5 w-2.5 md:h-3 md:w-3" />
                {formatChange(metrics.postsChange)}
              </div>
            )}
          </div>

          {/* Weekly Goal */}
          <div className={cn("group p-3 md:p-5 rounded-xl bg-gradient-to-br border-2 transition-all hover:scale-105 hover:shadow-lg relative overflow-hidden", wc.bg, wc.border)}>
            <div className={cn("absolute top-0 right-0 w-20 h-20 rounded-full blur-2xl", wc.glow)} />
            <div className="text-xl md:text-3xl font-bold text-foreground counter-animate flex items-center gap-1">
              {metrics?.postsPerWeek !== undefined && metrics.postsPerWeek !== null ? (
                metrics.postsPerWeek.toFixed(1)
              ) : metrics?.hasInsufficientData ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 cursor-help">
                        <Loader2 className={cn("h-5 w-5 animate-spin", wc.spinner)} />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Zbieramy dane do obliczenia średniej</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                '—'
              )}
            </div>
            <div className={cn("text-[10px] md:text-xs font-medium mt-1", wc.label)}>{t('dashboard.postsPerWeek')}</div>
            {metrics?.hasInsufficientData && !metrics?.postsPerWeek ? (
              <div className={cn("text-[10px] md:text-xs mt-1 md:mt-2 flex items-center gap-0.5 md:gap-1 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full bg-white/50 dark:bg-black/20 w-fit", wc.badgeMuted)}>
                <Info className="h-2.5 w-2.5 md:h-3 md:w-3" />
                Zbieramy dane...
              </div>
            ) : metrics?.postsGoal && (
              <div className={cn("text-[10px] md:text-xs mt-1 md:mt-2 flex items-center gap-0.5 md:gap-1 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full bg-white/50 dark:bg-black/20 w-fit font-bold", wc.badge)}>
                <Calendar className="h-2.5 w-2.5 md:h-3 md:w-3" />
                {t('dashboard.goal', { goal: String(metrics.postsGoal) })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs pt-4 border-t border-border/50">
          <div className="flex items-center gap-2 text-success">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
            <span className="font-medium">{t('dashboard.activeTracking')}</span>
          </div>
          <span className="text-muted-foreground">{t('dashboard.updatedAgo', { time: formatRelativeTime(profile.scraped_at, language) })}</span>
        </div>
      </CardContent>
    </div>
  );
};